import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { fmtMoney, fmtDate, EmptyState } from "@/components/Shared";
import {
  Users, Warning, CheckCircle, Clock, Plug, XCircle,
  ArrowsLeftRight, Lightning, CaretRight, Key, Eye, EyeSlash, FloppyDisk, Copy,
  MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BusinessDatePicker } from "@/components/BusinessDatePicker";
import { toast } from "sonner";

const TITLES = {
  active:                { title: "Clientes activos",         desc: "Al día, sin facturas vencidas" },
  pending:               { title: "Pendientes activación",    desc: "Nuevos clientes esperando activar servicio" },
  pending_reconnection:  { title: "Pendientes reconexión",    desc: "Pagaron y esperan que se restablezca el servicio" },
  disconnected:          { title: "Clientes desconectados",   desc: "Servicio suspendido por falta de pago" },
  transferred:           { title: "Clientes trasladados",     desc: "Contratos cerrados o trasladados a otro proveedor" },
  critical:              { title: "Mora crítica",             desc: "Clientes que requieren contacto inmediato" },
  all:                   { title: "Todos los clientes",       desc: "Vista completa del portafolio" },
};

function PaymentBadge({ client }) {
  if (client.status === "pending") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="badge-pending-activation"><Clock size={10} weight="bold" /> Pend. activación</span>;
  }
  if (client.status === "pending_reconnection") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="badge-pending-reconn"><Clock size={10} weight="bold" /> Pend. reconexión</span>;
  }
  if (client.status === "disconnected" || client.has_disconnected) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200" data-testid="badge-disconnected"><Warning size={10} weight="bold" /> Desconectado</span>;
  }
  if (client.status === "transferred") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" data-testid="badge-transferred"><ArrowsLeftRight size={10} weight="bold" /> Trasladado</span>;
  }
  if (client.overdue_invoices_count > 0) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" data-testid="badge-overdue"><Warning size={10} weight="bold" /> {client.overdue_invoices_count} vencida{client.overdue_invoices_count > 1 ? "s" : ""}</span>;
  }
  if ((client.pending_amount || 0) > 0) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" data-testid="badge-pending"><Clock size={10} weight="bold" /> Pendiente</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-paid"><CheckCircle size={10} weight="bold" /> Al día</span>;
}

export default function SupportClientsPage() {
  const { filter = "active" } = useParams();
  const meta = TITLES[filter] || TITLES.all;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/clients", { params: { filter } });
      setClients(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Reset search when switching between filters
  useEffect(() => { setSearch(""); }, [filter]);

  // Client-side filter — matches name, phone, email or address (case- & accent-insensitive)
  const normalize = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filteredClients = useMemo(() => {
    const q = normalize(search).trim();
    if (!q) return clients;
    return clients.filter((c) => {
      const hay = [
        c.name, c.first_name, c.last_name,
        c.email, c.phone,
        c.service_address, c.address,
        c.portal_username, c.account_number, c.rep_provider,
      ].map(normalize).join(" ");
      return hay.includes(q);
    });
  }, [clients, search]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/support/clients/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const transfer = async (id) => {
    const reason = prompt("Motivo del traslado (opcional):");
    if (reason === null) return;
    setBusyId(id);
    try { await api.post(`/support/clients/${id}/transfer`, { reason }); toast.success("Cliente trasladado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const reactivate = async (id) => {
    if (!confirm("¿Reactivar este cliente? Volverá a estado activo.")) return;
    setBusyId(id);
    try { await api.post(`/support/clients/${id}/reactivate`); toast.success("Cliente reactivado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const totalPending = filteredClients.reduce((s, c) => s + (c.pending_amount || 0), 0);

  // Color of the page accent based on filter
  const accentBar = {
    active:       "from-emerald-400 to-emerald-600",
    disconnected: "from-red-400 to-red-600",
    transferred:  "from-slate-400 to-slate-600",
    critical:     "from-amber-400 to-red-500",
    all:          "from-indigo-400 to-indigo-600",
  }[filter] || "from-slate-400 to-slate-600";

  return (
    <PageShell
      section="Support"
      page={meta.title}
      context={`${filteredClients.length}${search ? ` de ${clients.length}` : ""} · ${fmtMoney(totalPending)} pendiente`}
      testid="sclients-toolbar"
    >
      {/* Hero strip */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className={`h-1 bg-gradient-to-r ${accentBar}`} />
        <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{meta.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{meta.desc}</div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live search input */}
            <div className="relative w-64">
              <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, teléfono, dirección…"
                className="h-9 pl-8 pr-8 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                data-testid="sclient-search-input"
                aria-label="Buscar clientes"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-900 transition-colors"
                  aria-label="Limpiar búsqueda"
                  data-testid="sclient-search-clear"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
            <div className="text-right pl-3 border-l border-slate-200">
              <div className="text-2xl font-mono font-bold text-slate-900 tabular-nums" data-testid="sclient-count">{filteredClients.length}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {search ? `de ${clients.length}` : "en vista"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Cliente</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Contacto</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Último pago</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 text-right">Pendiente</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Próximo venc.</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Estado</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 text-right">Cambiar a</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && filteredClients.length === 0 && (
              <tr><td colSpan={7}>
                {search ? (
                  <EmptyState
                    icon={MagnifyingGlass}
                    title="Sin resultados"
                    message={`Ningún cliente coincide con "${search}". Prueba con otro término o revisa la ortografía.`}
                  />
                ) : (
                  <EmptyState icon={Users} title="Sin clientes en esta categoría" message="Cuando active o desconecte clientes los verás aquí." />
                )}
              </td></tr>
            )}
            {filteredClients.map((c) => {
              const critical = c.payment_alert_level === "critical" || c.status === "disconnected";
              const isExpanded = expandedId === c.id;
              const isPending = c.status === "pending";
              // Days waiting (used for aging alerts on pending-activation view)
              const daysWaiting = c.created_at
                ? Math.floor((Date.now() - new Date(c.created_at).getTime()) / (24 * 3600 * 1000))
                : 0;
              const isOverdue = isPending && daysWaiting >= 3;
              const isVeryOverdue = isPending && daysWaiting >= 7;
              const rowClass = isVeryOverdue
                ? "bg-red-50/50 hover:bg-red-50/70"
                : isOverdue
                  ? "bg-amber-50/40 hover:bg-amber-50/60"
                  : critical
                    ? "bg-red-50/40 hover:bg-red-50/60"
                    : "hover:bg-slate-50";
              return (
                <>
                <tr key={c.id} className={`transition-colors ${rowClass}`} data-testid={`sclient-row-${c.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        className="text-slate-400 hover:text-slate-900 transition-colors shrink-0"
                        aria-label="Ver credenciales"
                        data-testid={`sc-expand-${c.id}`}
                        title="Ver / editar credenciales del portal REP"
                      >
                        <CaretRight size={13} weight="bold" className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </button>
                      {critical && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                      {c.has_outage && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-300 shrink-0 animate-pulse" title={c.outage_note || "Cliente en interrupción"}>
                          <Lightning size={9} weight="fill" /> Sin luz
                        </span>
                      )}
                      <span className="truncate">{c.name}</span>
                      {isPending && daysWaiting > 0 && (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider shrink-0 ${
                            isVeryOverdue
                              ? "bg-red-100 text-red-800 border border-red-300 animate-pulse"
                              : isOverdue
                                ? "bg-amber-100 text-amber-800 border border-amber-300"
                                : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                          title={`Registrado hace ${daysWaiting} día${daysWaiting !== 1 ? "s" : ""}`}
                          data-testid={`aging-${c.id}`}
                        >
                          <Clock size={9} weight={isOverdue ? "fill" : "regular"} />
                          {daysWaiting}d
                        </span>
                      )}
                      {(c.portal_username || c.portal_password || c.account_number) && (
                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono bg-slate-100 text-slate-500 border border-slate-200" title="Credenciales guardadas">
                          <Key size={8} weight="fill" className="text-amber-500" />
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{fmtMoney(c.monthly_fee)} / mes</div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs"><div>{c.email || "—"}</div><div>{c.phone}</div></td>
                  <td className="px-5 py-3 text-slate-700">
                    {c.last_payment_date ? (<><div className="font-mono text-xs">{fmtDate(c.last_payment_date)}</div><div className="text-[11px] text-emerald-600 font-mono">{fmtMoney(c.last_payment_amount)}</div></>) : <span className="text-slate-400 text-xs">Sin pagos</span>}
                  </td>
                  <td className={`px-5 py-3 text-right font-mono ${c.pending_amount > 0 ? "text-red-700 font-semibold" : "text-slate-400"}`} data-testid={`pending-${c.id}`}>{fmtMoney(c.pending_amount || 0)}</td>
                  <td className="px-5 py-3 text-slate-600 text-xs font-mono">{c.next_due_date ? fmtDate(c.next_due_date) : <span className="text-slate-400">—</span>}</td>
                  <td className="px-5 py-3"><PaymentBadge client={c} /></td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      {isPending && (
                        <button
                          onClick={() => setStatus(c.id, "active")}
                          disabled={busyId === c.id}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
                          data-testid={`quick-activate-${c.id}`}
                          title="Activar cliente ahora"
                        >
                          <CheckCircle size={11} weight="fill" />
                          Activar
                        </button>
                      )}
                      <select
                        value={c.status || "pending"}
                        onChange={(e) => setStatus(c.id, e.target.value)}
                        disabled={busyId === c.id}
                        className="text-xs font-medium border border-slate-200 px-2 py-1 bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 disabled:opacity-50 cursor-pointer"
                        data-testid={`status-select-${c.id}`}
                      >
                        <option value="pending">Pendiente activación</option>
                        <option value="active">Activo</option>
                        <option value="cancelled">Rechazado / Cancelado</option>
                        <option value="disconnected">Desconectado</option>
                        <option value="transferred">Trasladado</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <ClientCredentialsRow client={c} onSaved={load} />
                )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}


/** ClientCredentialsRow — Support-only credential editor for the REP portal login.
 *  Financial admins do NOT see this data; it lives solely on the Support side.
 */
function ClientCredentialsRow({ client, onSaved }) {
  const [form, setForm] = useState({
    first_name: client.first_name || "",
    last_name: client.last_name || "",
    email: client.email || "",
    portal_username: client.portal_username || "",
    portal_password: client.portal_password || "",
    account_number: client.account_number || "",
    rep_provider: client.rep_provider || "",
    service_start_date: client.service_start_date || "",
    manual_last_payment_date: client.manual_last_payment_date || (client.last_payment_manual ? client.last_payment_date?.slice(0, 10) : "") || "",
    manual_last_payment_amount: client.manual_last_payment_amount ?? (client.last_payment_manual ? client.last_payment_amount : "") ?? "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get(`/support/clients/${client.id}/credentials-history`)
      .then((r) => setHistory(r.data?.history || []))
      .catch(() => {});
  }, [client.id]);

  const save = async () => {
    setSaving(true);
    try {
      // Build payload — only send fields the user actually filled.
      // Empty strings are treated as "not provided" so nothing is required.
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        payload[k] = v;
      }
      // Coerce numeric field
      if (payload.manual_last_payment_amount !== undefined) {
        payload.manual_last_payment_amount = Number(payload.manual_last_payment_amount);
        if (!Number.isFinite(payload.manual_last_payment_amount)) delete payload.manual_last_payment_amount;
      }
      await api.patch(`/support/clients/${client.id}/credentials`, payload);
      toast.success("Datos guardados · visibles solo para Support");
      // Refresh audit log
      api.get(`/support/clients/${client.id}/credentials-history`)
        .then((r) => setHistory(r.data?.history || []))
        .catch(() => {});
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const copy = (v, label) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success(`${label} copiado`);
  };

  return (
    <tr className="bg-slate-50/60" data-testid={`sc-credentials-${client.id}`}>
      <td colSpan={7} className="px-5 py-5 border-t border-slate-200">
        <div className="max-w-5xl">
          <div className="flex items-center gap-2 mb-3">
            <Key size={13} weight="fill" className="text-amber-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-700">
              Datos del cliente y credenciales del portal REP
            </div>
            <span className="text-[10px] text-slate-500">· visible solo para Support</span>
            {history.length > 0 && (
              <button
                onClick={() => setShowHistory(v => !v)}
                className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 font-semibold"
                data-testid={`sc-toggle-history-${client.id}`}
              >
                <Clock size={11} />
                {showHistory ? "Ocultar historial" : `Historial (${history.length})`}
              </button>
            )}
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nombre</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                data-testid={`sc-first-${client.id}`}
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Apellido</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                data-testid={`sc-last-${client.id}`}
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Correo</Label>
              <div className="relative">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="cliente@ejemplo.com"
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 pr-8"
                  data-testid={`sc-email-${client.id}`}
                />
                {form.email && (
                  <button onClick={() => copy(form.email, "Correo")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Copiar">
                    <Copy size={12} />
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Usuario del portal</Label>
              <div className="relative">
                <Input
                  value={form.portal_username}
                  onChange={(e) => setForm({ ...form, portal_username: e.target.value })}
                  placeholder="ej. john.doe@rhythm.com"
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 pr-8"
                  data-testid={`sc-username-${client.id}`}
                />
                {form.portal_username && (
                  <button onClick={() => copy(form.portal_username, "Usuario")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Copiar">
                    <Copy size={12} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contraseña</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={form.portal_password}
                  onChange={(e) => setForm({ ...form, portal_password: e.target.value })}
                  placeholder="••••••••"
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 pr-14 font-mono"
                  data-testid={`sc-password-${client.id}`}
                />
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button onClick={() => setShowPw(!showPw)} className="p-1 text-slate-400 hover:text-slate-700" title={showPw ? "Ocultar" : "Mostrar"}>
                    {showPw ? <EyeSlash size={12} /> : <Eye size={12} />}
                  </button>
                  {form.portal_password && (
                    <button onClick={() => copy(form.portal_password, "Contraseña")} className="p-1 text-slate-400 hover:text-slate-700" title="Copiar">
                      <Copy size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Número de cuenta</Label>
              <div className="relative">
                <Input
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  placeholder="ESI ID / Account #"
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 pr-8 font-mono"
                  data-testid={`sc-account-${client.id}`}
                />
                {form.account_number && (
                  <button onClick={() => copy(form.account_number, "Número de cuenta")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Copiar">
                    <Copy size={12} />
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Proveedor (REP)</Label>
              <div className="relative">
                <Input
                  value={form.rep_provider}
                  onChange={(e) => setForm({ ...form, rep_provider: e.target.value })}
                  placeholder="Rhythm, TXU, Reliant…"
                  list={`rep-providers-${client.id}`}
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 pr-8"
                  data-testid={`sc-rep-${client.id}`}
                />
                {form.rep_provider && (
                  <button onClick={() => copy(form.rep_provider, "Proveedor")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Copiar">
                    <Copy size={12} />
                  </button>
                )}
                <datalist id={`rep-providers-${client.id}`}>
                  <option value="Rhythm Energy" />
                  <option value="TXU Energy" />
                  <option value="Reliant Energy" />
                  <option value="Gexa Energy" />
                  <option value="Green Mountain Energy" />
                  <option value="Direct Energy" />
                  <option value="Constellation" />
                  <option value="Discount Power" />
                  <option value="4Change Energy" />
                  <option value="Payless Power" />
                </datalist>
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha de inicio de servicio</Label>
              <BusinessDatePicker
                value={form.service_start_date}
                onChange={(iso) => setForm({ ...form, service_start_date: iso })}
                disableWeekends={{ saturday: false, sunday: false }}
                placeholder="MM/DD/AAAA"
                className="mt-1"
                data-testid={`sc-service-start-${client.id}`}
              />
            </div>

            {/* Editable manual override for last payment (Support-only) */}
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1">
                  Último pago · fecha
                  <span className="text-[9px] normal-case tracking-normal text-slate-400 font-normal">(manual · sobrescribe Square)</span>
                </Label>
                <BusinessDatePicker
                  value={form.manual_last_payment_date}
                  onChange={(iso) => setForm({ ...form, manual_last_payment_date: iso })}
                  disableWeekends={{ saturday: false, sunday: false }}
                  placeholder="MM/DD/AAAA"
                  className="mt-1"
                  data-testid={`sc-lastpay-date-${client.id}`}
                />
                {client.last_payment_date && !form.manual_last_payment_date && (
                  <div className="mt-1.5 text-[10px] text-slate-400 font-mono">
                    Auto (Square): {fmtDate(client.last_payment_date)} · {fmtMoney(client.last_payment_amount || 0)}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Monto (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.manual_last_payment_amount}
                  onChange={(e) => setForm({ ...form, manual_last_payment_amount: e.target.value })}
                  className="h-9 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 font-mono mt-1"
                  data-testid={`sc-lastpay-amount-${client.id}`}
                />
              </div>
            </div>
          </div>

          {/* Audit log */}
          {showHistory && history.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3" data-testid={`sc-history-${client.id}`}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                <Clock size={11} />
                Historial de cambios ({history.length})
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] px-2 py-1.5 bg-white border border-slate-200 rounded">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{h.agent_email || "—"}</span>
                        <span className="text-slate-400 font-mono">{fmtDate(h.created_at)}</span>
                      </div>
                      <div className="text-slate-500 mt-0.5">
                        Cambió: <span className="font-mono text-slate-700">{(h.fields_changed || []).filter(f => f !== "credentials_updated_at" && f !== "credentials_updated_by").join(", ") || "—"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white h-9"
              data-testid={`sc-save-${client.id}`}
            >
              <FloppyDisk size={13} weight="fill" className="mr-1.5" />
              {saving ? "Guardando…" : "Guardar datos"}
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
}
